import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { useOverlayContext, useStateInRef } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { useRootDevice } from '~/providers/DeviceProvider';
import { useAppConfig, useStoreActions, useStoreStatic } from '~/store/store-context';
import { FeatureFeedbackModal } from '../FeatureFeedbackModal';
import { userToPromptFor, composeFeatureFeedbackText, FEATURE_FEEDBACK_PROMPT_RETRY_DELAY_MS, featureLeadingTexts } from './useFeatureFeedbackPrompt.utils';
function runWhenNoDialogIsOpen(schedule) {
    if (!schedule.hasOpenDialog()) {
        schedule.run();
        return;
    }
    schedule.remember(setTimeout(()=>runWhenNoDialogIsOpen(schedule), schedule.delayMs));
}
const feedbackModalContent = (props)=>(param)=>{
        let { dismissModal } = param;
        return /*#__PURE__*/ _jsx(FeatureFeedbackModal, _object_spread_props(_object_spread({}, props), {
            onClose: ()=>dismissModal()
        }));
    };
export function useFeatureFeedbackPrompt() {
    const { showModal, getOpenedModals } = useOverlayContext();
    const { isMobileDevice } = useRootDevice();
    const { api, updateUserClientData } = useStoreActions();
    const { getState } = useStoreStatic();
    const { featureFeedbackPromptRetryDelayMs: delayMs = FEATURE_FEEDBACK_PROMPT_RETRY_DELAY_MS } = useAppConfig();
    const { t: bulkTexts } = useLocaleKeys('proposal.bulkCreateDraftsFeedback');
    const getLeadingTexts = useStateInRef(featureLeadingTexts(bulkTexts));
    const getIsMobileDevice = useStateInRef(isMobileDevice);
    const claimedFeaturesRef = useRef(new Set());
    const retryTimersRef = useRef(new Set());
    const hasOpenDialog = ()=>getOpenedModals().length > 0;
    const rememberTimer = (timer)=>retryTimersRef.current.add(timer);
    const userToPrompt = (feature)=>getIsMobileDevice() || claimedFeaturesRef.current.has(feature) ? null : userToPromptFor(getState(), feature);
    useEffect(()=>()=>retryTimersRef.current.forEach(clearTimeout), []);
    const openPrompt = useCallback((request, userInfo)=>{
        const { userClientData } = getState();
        void updateUserClientData({
            featureFeedbackPrompted: _object_spread_props(_object_spread({}, userClientData === null || userClientData === void 0 ? void 0 : userClientData.featureFeedbackPrompted), {
                [request.feature]: true
            })
        });
        const onSubmit = (submission)=>api.slack.sendFeatureFeedback(composeFeatureFeedbackText(request, submission, userInfo));
        const content = feedbackModalContent(_object_spread_props(_object_spread({}, getLeadingTexts()[request.feature]), {
            onSubmit
        }));
        void showModal({
            biName: 'feature_feedback',
            content
        });
    }, []);
    return useCallback((request)=>{
        const userInfo = userToPrompt(request.feature);
        if (userInfo == null) return;
        claimedFeaturesRef.current.add(request.feature);
        runWhenNoDialogIsOpen({
            hasOpenDialog,
            delayMs,
            run: ()=>openPrompt(request, userInfo),
            remember: rememberTimer
        });
    }, [
        openPrompt
    ]);
}
